<template>
  <BittsModal
    :width="1200"
    :visible="true"
    :use-mask-to-close="true"
    no-padding
    :title
    :loading
    variant="sidebar"
    :disabled="loading || !hasChanges || hasOverrideWithoutFields"
    :primary-button-text="alreadyHasOverrides ? 'Save' : 'Create'"
    :action-loading="isSaving"
    @closed="onCloseModal"
    @action="onSave"
  >
    <template #sidebar-header>
      <div
        v-if="!loading"
        class="flex w-full items-center justify-between px-16"
      >
        <p class="text-neutral-text-strong font-bold">
          {{ overridePopulationIds.length }}
          {{ pluralize(overridePopulationIds.length, 'Override') }}
        </p>
        <BittsDropdown
          :class="{ open: showAddOverrideDropdown }"
          :options
          :prevent-open="hasNoOptionsLeft"
          :header-bold="true"
          @selection="onAddNewOverride"
        >
          <template #prompt>
            <BittsButton
              :left-icon="['fak', 'add']"
              text="Add an override"
              variant="outline"
              :disabled="hasNoOptionsLeft"
              size="small"
              @click="showAddOverrideDropdown = !showAddOverrideDropdown"
            />
          </template>
        </BittsDropdown>
      </div>
    </template>
    <template #sidebar-content>
      <div class="flex flex-col gap-8 w-full">
        <button
          v-for="popId in overridePopulationIds"
          :key="`override__${popId}`"
          :class="{
            'c-pop-overrides-modal__override--active':
              selectedPartnerPopulationId === popId,
          }"
          class="c-pop-overrides-modal__override"
          type="button"
          @click="selectOverride(popId)"
        >
          <div class="text-left">
            <p class="font-bold text-neutral-text-strong">
              {{ getPopulationName(popId) }}
            </p>
            <p class="font-normal text-neutral-text-weak text-sm">
              {{ getFieldsCount(popId) }} fields
            </p>
          </div>
          <FontAwesomeIcon
            class="self-center text-primary-accent"
            :icon="['fak', 'chevron-right']"
            :style="{
              height: '16px',
              width: '16px',
              color: 'currentColor',
            }"
          />
        </button>
      </div>
    </template>
    <div class="c-pop-overrides-modal__content min-h-[600px]">
      <div v-if="selectedPartnerPopulationId">
        <div class="flex justify-between w-full">
          <p
            >When overlapping {{ partnerName }}'s
            <strong>{{ getPopulationName(selectedPartnerPopulationId) }}</strong
            >, share
          </p>
          <BittsButton
            data-testid="delete-override-button"
            variant="outline"
            size="x-small"
            text="Remove Override"
            type="danger"
            class="text-neutral-text-weak"
            @click="onRemoveOverride"
          />
        </div>
        <BittsDivider class="my-16" />
        <p
          v-if="fieldHint"
          data-testid="override-needs-field"
          class="text-sm text-neutral-text-weak"
        >
          {{ fieldHint }}
        </p>
        <DataSharingFieldSelector
          data-testid="override-selection-tree"
          class="mt-16"
          :show-greenfield-tag="false"
          :selected-fields="localOverrides[selectedPartnerPopulationId] || []"
          @fields-selected="onFieldsSelected"
        />
      </div>
      <BittsEmptyState
        v-else
        title="Create an override by selecting a population from the “Add an override” menu"
        class="custom-population-overrides__empty-state"
        font-awesome-icon-class="custom-population-overrides__empty-state-icon"
        :font-awesome-icon="['fak', 'population']"
        :font-awesome-icon-style="{ height: '40px', width: '40px' }"
      >
        <template #subtitle>
          <div class="text-neutral-text-weak text-sm">
            An override enables you to selectively share data only in the case
            of an overlap with a specific {{ partnerName }} population
          </div>
        </template>
      </BittsEmptyState>
    </div>
  </BittsModal>
</template>

<script setup lang="ts">
import {
  BittsButton,
  BittsDivider,
  BittsDropdown,
  BittsEmptyState,
  BittsModal,
} from '@crossbeam/bitts';

import { pluralize } from 'humanize-plus';
import { cloneDeep, isEqual } from 'lodash';
import type { ComputedRef } from 'vue';
import { computed, inject, nextTick, onMounted, provide, ref } from 'vue';
import { useRoute } from 'vue-router';

import DataSharingFieldSelector from '@/components/partners/DataSharingSettings/DataSharingFieldSelector.vue';

import useDataSharing from '@/composables/useDataSharing';
import useGreenfieldSharing from '@/composables/useGreenfieldSharing';
import usePopulations from '@/composables/usePopulations';
import { ProvidedGreenfieldSourceFields } from '@/injectionKeys/data_shares';
import {
  useDataRequestsStore,
  useDataSharesStore,
  useFlashesStore,
  usePartnersStore,
  usePopulationsStore,
  useSourcesStore,
} from '@/stores';
import { formatList } from '@/utils';

const { populationId } = defineProps<{
  populationId: number | null;
}>();

const emit = defineEmits<(e: 'closed') => void>();

interface OverrideToDelete {
  partnerPopId: number;
  dataShareId: number | null;
}

interface PopulationToPopulationSetting {
  partnerPopulation: {
    id: number;
    name: string;
  };
  dataShare: {
    id: number;
  };
}

const dataSharesStore = useDataSharesStore();
const { refreshDataRequestsStore } = useDataRequestsStore();
const sourcesStore = useSourcesStore();
const partnersStore = usePartnersStore();
const populationsStore = usePopulationsStore();
const flashesStore = useFlashesStore();
const route = useRoute();

// Inject partner ID from props mode (when opened from SharingSettingsPopulation)
// Otherwise fall back to route params
const propsPartnerId = inject<ComputedRef<number | null>>(
  'propsPartnerId',
  computed(() => null),
);
const partnerId = computed(
  () => propsPartnerId.value ?? Number(route.params.partner_org_id),
);

// Session key for store access
const sessionKey = computed(() =>
  dataSharesStore.getSessionKey(partnerId.value),
);

const { customPopsWithStatsAndSharing, allPopulations: standardPopulations } =
  usePopulations(partnerId.value as never);

const allPopulations = computed(() => [
  ...standardPopulations.value,
  ...customPopsWithStatsAndSharing.value,
]);

const population = computed(() => {
  return allPopulations.value.find((pop) => pop.id === populationId);
});

const showAddOverrideDropdown = ref(false);
const localOverrides = ref<Record<number, number[]>>({});
const initialOverrides = ref<Record<number, number[]>>({});
const selectedPartnerPopulationId = ref<number | null | undefined>(null);
const overridesToDelete = ref<OverrideToDelete[]>([]);

const isSaving = ref(false);
const loading = ref(false);

const alreadyHasOverrides = computed(
  () => !!Object.keys(initialOverrides.value).length,
);

const partnerPops = computed(() =>
  populationsStore.getPartnerPopulationsByOrg(partnerId.value),
);

const partnerName = computed(
  () => partnersStore.getPartnerOrgById(partnerId.value)?.name,
);

const title = computed(
  () =>
    `${alreadyHasOverrides.value ? 'Edit' : 'Create'} overrides for ${population.value?.name} (${population.value?.standard_type ? 'Standard Population' : 'Custom Population'})`,
);

useDataSharing({
  population: population.value,
  partnerId: partnerId.value,
});

// we need to provide the greenfield fields to the DataSharingFieldSelector component
const { reqdGreenfieldFieldIds, setRequiredGreenfieldFieldIds } =
  useGreenfieldSharing();
provide(ProvidedGreenfieldSourceFields, reqdGreenfieldFieldIds);

// Access store's editing state directly
const currentPopulationToPopulation = computed({
  get: () => dataSharesStore.currentPopToPopMap[sessionKey.value] || {},
  set: (value) => {
    dataSharesStore.currentPopToPopMap[sessionKey.value] = value;
  },
});

function onCloseModal() {
  emit('closed');
}

const overridePopulationIds = computed(() => {
  if (!localOverrides.value) {
    return [];
  }
  return Object.keys(localOverrides.value).map((id) => Number(id));
});

const options = computed(() => {
  const popOptions = partnerPops.value
    .filter((pop) => !overridePopulationIds.value.includes(pop.id))
    .map((pop) => ({
      display: pop.name,
      value: pop.id,
    }));
  return ['Select a Population', ...popOptions];
});

const selectedOverride = computed(() => {
  const popId = selectedPartnerPopulationId.value;
  return {
    id: popId,
    dataShareId: popId
      ? population.value?.populationToPopulationSettings.find(
          (s: PopulationToPopulationSetting) =>
            s.partnerPopulation.id === popId,
        )?.dataShare.id
      : null,
  };
});

const hasNoOptionsLeft = computed(() => options.value.length === 1);

const hasChanges = computed(() => {
  return (
    !isEqual(localOverrides.value, initialOverrides.value) ||
    overridesToDelete.value.length > 0
  );
});

/**
 * The store leaves an override with no fields out of the save. Block one the
 * user added, emptied or removed and re-added here, but not one already saved
 * that way, so it does not lock every other edit.
 */
const blockingOverrideIds = computed(() => {
  const removedIds = new Set(
    overridesToDelete.value.map(({ partnerPopId }) => partnerPopId),
  );
  return Object.entries(localOverrides.value)
    .map(([partnerPopId, fields]) => ({ id: Number(partnerPopId), fields }))
    .filter(
      ({ id, fields }) =>
        !fields.length &&
        (removedIds.has(id) || !isEqual(fields, initialOverrides.value[id])),
    )
    .map(({ id }) => id);
});

const hasOverrideWithoutFields = computed(
  () => !!blockingOverrideIds.value.length,
);

const fieldHint = computed(() => {
  const blockingIds = blockingOverrideIds.value;
  if (!blockingIds.length) return null;
  const selectedId = selectedPartnerPopulationId.value;
  if (selectedId && blockingIds.includes(selectedId)) {
    return 'Pick at least one field to save this override';
  }
  return `Pick at least one field for ${formatList(blockingIds.map(getPopulationName))} to save`;
});

function getPopulationName(popId: number) {
  const population = partnerPops.value?.find((pop) => pop.id === popId);
  return population ? population.name : `Population ${popId}`;
}

function getFieldsCount(popId: number) {
  return localOverrides.value[popId]?.length || 0;
}

function selectOverride(popId: number) {
  selectedPartnerPopulationId.value = popId;
}

function onAddNewOverride({ value }: { value: number }) {
  if (!value) return;
  const updatedOverrides = { ...localOverrides.value };
  updatedOverrides[value] = [];
  localOverrides.value = updatedOverrides;
  selectedPartnerPopulationId.value = value;
}

function onRemoveOverride() {
  if (!selectedPartnerPopulationId.value) return;
  overridesToDelete.value.push({
    partnerPopId: selectedPartnerPopulationId.value,
    dataShareId: selectedOverride.value.dataShareId,
  });
  const updatedOverrides = { ...localOverrides.value };
  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
  delete updatedOverrides[selectedPartnerPopulationId.value];
  localOverrides.value = updatedOverrides;
  const remainingIds = Object.keys(updatedOverrides).map(Number) || [];
  selectedPartnerPopulationId.value = remainingIds?.length
    ? remainingIds[0]
    : null;
}

function onFieldsSelected(fields: number[]) {
  if (!selectedPartnerPopulationId.value) return;
  const updatedOverrides = { ...localOverrides.value };
  updatedOverrides[selectedPartnerPopulationId.value] = [...fields];
  localOverrides.value = updatedOverrides;
}

async function onSave() {
  if (!hasChanges.value || !populationId) return;

  isSaving.value = true;

  try {
    // Delete overrides marked for deletion
    for (const { partnerPopId, dataShareId } of overridesToDelete.value) {
      dataSharesStore.deletePopToPopOverride(
        partnerId.value,
        Number(dataShareId),
        Number(partnerPopId),
        populationId,
      );
    }

    // Update overrides in store
    Object.entries(localOverrides.value).forEach(([partnerPopId, fields]) => {
      if (!partnerPopId) return;
      dataSharesStore.updatePopToPopOverride(
        partnerId.value,
        populationId,
        Number(partnerPopId),
        fields,
      );
    });

    // Save all changes
    await dataSharesStore.bulkSaveChanges(partnerId.value);
    await refreshDataRequestsStore();
    flashesStore.addSuccessFlash({ message: 'Settings saved' });

    // Reinitialize editing session to sync initial state with saved state
    dataSharesStore.initializeEditingSession(
      partnerId.value,
      allPopulations.value,
    );

    overridesToDelete.value = [];
    initialOverrides.value = cloneDeep(localOverrides.value);
  } catch (error) {
    flashesStore.addErrorFlash({ message: 'Error saving overrides' });
  } finally {
    isSaving.value = false;
    onCloseModal();
  }
}

onMounted(async () => {
  loading.value = true;
  await Promise.all([
    dataSharesStore.refreshDataSharesStore(),
    sourcesStore.refreshSourcesStore(),
    partnersStore.refreshPartnersStore(),
    populationsStore.refreshPopulationsStore(),
  ]);

  // Wait for next tick to ensure all computed properties have updated
  // with the refreshed store data
  await nextTick();

  if (!population.value || !populationId) return;

  // Always re-initialize editing session with fresh store data
  // This ensures we have the latest population-to-population overrides
  dataSharesStore.initializeEditingSession(
    partnerId.value,
    allPopulations.value,
  );

  if (!sourcesStore.popSourceIdToSourcesMap[population.value.source_id]) {
    await sourcesStore.setSourcesByPopulationSourceId(population.value);
  }
  setRequiredGreenfieldFieldIds(
    population.value.population_type,
    sourcesStore.popSourceIdToSourcesMap[population.value.source_id],
  );

  if (currentPopulationToPopulation.value?.[populationId]) {
    localOverrides.value = cloneDeep(
      currentPopulationToPopulation.value[populationId],
    );
    initialOverrides.value = cloneDeep(
      currentPopulationToPopulation.value[populationId],
    );

    const firstPartnerPopId = Object.keys(localOverrides.value)[0];
    if (firstPartnerPopId) {
      selectedPartnerPopulationId.value = Number(firstPartnerPopId);
    }
  } else {
    localOverrides.value = {};
    initialOverrides.value = {};
  }

  loading.value = false;
});
</script>

<style scoped lang="pcss">
@reference "../../../../../../css/theme.css";
.c-pop-overrides-modal__content {
  @apply h-[640px] p-16;
  .c-bitts-empty-state-large-border {
    @apply border-none h-full;
  }
}
.c-pop-overrides-modal__override {
  @apply flex justify-between rounded-[10px] border-2 border-solid border-neutral-border bg-white p-12;
}
.c-pop-overrides-modal__override--active {
  @apply border-neutral-border-focus bg-primary-background-weak;
}
</style>
